import { useState, type FormEvent } from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router'
import { LogIn, Mail } from 'lucide-react'
import { useAuth } from '@/auth/useAuth'
import { getAssuranceState } from '@/auth/mfa'
import { MfaStepUpDialog } from '@/features/mfa/MfaStepUpDialog'
import { Button } from '@/components/ui/button'
import {
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import logoRgb from '@/assets/brand/Logo_RGB.png'

/**
 * Only allow same-app, absolute-path redirects (e.g. `/founder/forms/123`).
 * Guards against open-redirect via a crafted `?redirect=` (spec-14 G11 hardens
 * this further); anything not starting with a single `/` is ignored.
 */
function safeRedirectPath(raw: string | null): string | undefined {
  if (!raw) return undefined
  if (!raw.startsWith('/') || raw.startsWith('//')) return undefined
  return raw
}

export function LoginPage() {
  const { session, signIn, signInWithMagicLink } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const redirectPath = safeRedirectPath(searchParams.get('redirect'))
  // Magic-link (passwordless) sign-in is only for founders, who have no password
  // and arrive deep-linked at `/login?redirect=/founder/forms/<id>`. Admin/team on
  // the plain login page are password-only (+ MFA step-up).
  const isFounderContext = redirectPath?.startsWith('/founder') ?? false

  const [mode, setMode] = useState<'password' | 'magic'>('password')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [magicLinkSent, setMagicLinkSent] = useState(false)
  // When a verified factor exists, password sign-in leaves the session at aal1
  // and we prompt for the TOTP code before entering the app (spec-14b Phase 2).
  const [stepUpOpen, setStepUpOpen] = useState(false)

  // While the step-up dialog is open the session technically exists, so suppress
  // the auto-navigate below until the challenge is resolved (elevated/cancelled).
  if (session && !stepUpOpen) {
    return <Navigate to={redirectPath ?? '/'} replace />
  }

  function enterApp() {
    void navigate(redirectPath ?? '/', { replace: true })
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setSubmitting(true)
    setError(null)
    const result = await signIn(email, password)
    if (result.error) {
      setSubmitting(false)
      setError('Inloggen mislukt. Controleer je gegevens.')
      return
    }
    // A verified factor exists → step up to aal2 before entering the app. With
    // no factor the session stays aal1 and we navigate straight in (the
    // StaffMfaGate then forces enrolment before the shell opens).
    const assurance = await getAssuranceState()
    setSubmitting(false)
    if (assurance.needsStepUp) {
      setStepUpOpen(true)
      return
    }
    enterApp()
  }

  async function handleMagicLink(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setSubmitting(true)
    setError(null)
    const result = await signInWithMagicLink(email, redirectPath)
    setSubmitting(false)
    if (result.error) {
      setError('Versturen van de magic link is mislukt. Probeer het opnieuw.')
      return
    }
    setMagicLinkSent(true)
  }

  return (
    <main className="flex min-h-svh items-center justify-center p-6">
      <div className="vonk-card flex w-full max-w-md flex-col gap-4">
        <CardHeader className="text-center">
          <img src={logoRgb} alt="VONK Ventures" className="mx-auto mb-4 w-4/5" />
          <CardTitle className="text-xl">VONK Platform</CardTitle>
          <CardDescription>
            {mode === 'magic' && isFounderContext
              ? 'Log in zonder wachtwoord via een magic link'
              : 'Log in met je VONK-account'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {mode === 'password' || !isFounderContext ? (
            <form
              onSubmit={(event) => void handleSubmit(event)}
              className="flex flex-col gap-4"
            >
              <div className="flex flex-col gap-2">
                <Label htmlFor="email">E-mailadres</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="password">Wachtwoord</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </div>
              {error && (
                <p role="alert" className="text-sm text-destructive">
                  {error}
                </p>
              )}
              <Button
                type="submit"
                size="lg"
                disabled={submitting}
                className="w-full text-base transition hover:-translate-y-0.5"
              >
                <LogIn className="size-5" />
                {submitting ? 'Bezig met inloggen…' : 'Inloggen'}
              </Button>
              {isFounderContext && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setMode('magic')
                    setError(null)
                  }}
                >
                  Inloggen met een magic link
                </Button>
              )}
            </form>
          ) : magicLinkSent ? (
            <div className="flex flex-col gap-4 text-center">
              <p role="status" className="text-sm text-foreground">
                Check je inbox — we hebben een inloglink gestuurd naar{' '}
                <strong>{email}</strong>. Klik op de link om in te loggen.
              </p>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setMode('password')
                  setMagicLinkSent(false)
                  setError(null)
                }}
              >
                Terug naar inloggen met wachtwoord
              </Button>
            </div>
          ) : (
            <form
              onSubmit={(event) => void handleMagicLink(event)}
              className="flex flex-col gap-4"
            >
              <div className="flex flex-col gap-2">
                <Label htmlFor="magic-email">E-mailadres</Label>
                <Input
                  id="magic-email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </div>
              {error && (
                <p role="alert" className="text-sm text-destructive">
                  {error}
                </p>
              )}
              <Button
                type="submit"
                size="lg"
                disabled={submitting}
                className="w-full text-base transition hover:-translate-y-0.5"
              >
                <Mail className="size-5" />
                {submitting ? 'Bezig met versturen…' : 'Stuur mij een magic link'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setMode('password')
                  setError(null)
                }}
              >
                Inloggen met wachtwoord
              </Button>
            </form>
          )}
        </CardContent>
      </div>
      <MfaStepUpDialog
        open={stepUpOpen}
        onElevated={() => {
          setStepUpOpen(false)
          enterApp()
        }}
        onCancel={() => {
          // Cancelling keeps the user at aal1 (not a hard fail): they land on
          // the StaffMfaGate, which blocks the shell until they step up there.
          setStepUpOpen(false)
          enterApp()
        }}
      />
    </main>
  )
}
